import {useTranslation} from 'react-i18next';
import {BookingActions, useBookingContext} from '../../../../context/BookingContext';
import {useBookingSessionContext} from '../../../../context/BookingSessionContext';
import {DataActions, useClinicDetailsContext} from '../../../../context/ClinicDetailsContext';
import {
    fetchAppointmentType,
    fetchClinicById,
    fetchReserveTimeSlot,
    fetchUnreserveTimeSlot
} from '../../../../services/api/api';
import {useLocationDetailsContext} from 'src/context/LocationDetailsContext';
import {ClinicJSON} from 'src/services/api/types/types';

export function TimeSelectNextButton({className}: {className?: string}) {
    const {t} = useTranslation();
    const {
        dispatch,
        bookingState: {timeSlotState, reasonState, bookingId}
    } = useBookingContext();
    const {refreshBookingSession, startOpenSession} = useBookingSessionContext();

    const {
        locationDataState: {location, locationTimeSlots}
    } = useLocationDetailsContext();

    const {
        dataState: {clinic},
        dispatch: clinicDispatch
    } = useClinicDetailsContext();

    const onNextClick = async () => {
        if (!location?.id || !reasonState.appointmentType?.id || !timeSlotState.selectedSlot) {
            return;
        }

        dispatch({
            type: BookingActions.SET_TIMESLOT_DATA,
            value: {
                lockLoading: true,
                lockError: null
            }
        });

        let currentClinic = clinic;

        // Identify clinicId, then proceed as usual
        const selectedTimeSlot = locationTimeSlots.find((timeSlot) => {
            const timeSlotDate = new Date(timeSlot.when);

            return timeSlotDate.getTime() === timeSlotState?.selectedSlot?.getTime();
        });

        if (!selectedTimeSlot) {
            dispatch({
                type: BookingActions.SET_TIMESLOT_DATA,
                value: {
                    lockLoading: false,
                    lockError: new Error('Unable to reserve selected time slot.')
                }
            });
            return;
        }

        try {
            const clinicId = selectedTimeSlot?.clinic;

            if (!clinicId) {
                dispatch({
                    type: BookingActions.SET_TIMESLOT_DATA,
                    value: {
                        lockLoading: false,
                        lockError: new Error('Unable to reserve selected time slot.')
                    }
                });
                return;
            }

            const fetchedClinic = await fetchClinicById(clinicId);
            currentClinic = fetchedClinic;

            const apptType = await fetchAppointmentType(clinicId, selectedTimeSlot.appointmentTypeId);
            if (!apptType) {
                dispatch({
                    type: BookingActions.SET_TIMESLOT_DATA,
                    value: {
                        lockLoading: false,
                        lockError: new Error('Unable to reserve selected time slot.')
                    }
                });
                return;
            }

            clinicDispatch({
                type: DataActions.SET_CLINIC,
                value: fetchedClinic as ClinicJSON
            });

            // Yes, we replace the selected reason data here middle of booking flow
            // NOTE: This is not done for booking on a clinic but we need it here
            // to use the correct apppointment type (+ HD + allowed age)
            dispatch({
                type: BookingActions.SET_REASON_DATA,
                value: {
                    ...reasonState,
                    appointmentType: apptType,
                    healthDeclarationDefinitionId: apptType.category.healthDeclarationDefinitionId,
                    allowedAge: {minAge: apptType.category.minAge || null, maxAge: apptType.category.maxAge || null}
                }
            });
        } catch (error) {
            dispatch({
                type: BookingActions.SET_TIMESLOT_DATA,
                value: {
                    lockLoading: false,
                    lockError: error as Error
                }
            });
        }

        if (bookingId && currentClinic) {
            dispatch({type: BookingActions.CLEAR_BOOKING_ID});

            try {
                await fetchUnreserveTimeSlot(currentClinic.id, bookingId);
            } catch (error) {
                // Probably fine, the time might already be cleared
                console.error(error);
            }
        }

        await refreshBookingSession();

        try {
            if (!currentClinic?.id) {
                dispatch({
                    type: BookingActions.SET_TIMESLOT_DATA,
                    value: {
                        lockLoading: false,
                        lockError: new Error('Unable to reserve selected time slot.')
                    }
                });
                return;
            }
            const {bookingId, sessionDuration} = await fetchReserveTimeSlot(
                currentClinic.id,
                selectedTimeSlot.appointmentTypeId,
                timeSlotState.selectedSlot
            );

            if (reasonState.appointmentType.eidRequired === false) {
                startOpenSession(sessionDuration);
            }

            dispatch({type: BookingActions.SET_BOOKING_ID, value: bookingId});
            dispatch({
                type: BookingActions.SET_TIMESLOT_DATA,
                value: {
                    lockLoading: false,
                    lockError: null
                }
            });
            dispatch({type: BookingActions.NEXT_STEP});
        } catch (error) {
            dispatch({
                type: BookingActions.SET_TIMESLOT_DATA,
                value: {
                    lockLoading: false,
                    lockError: error as Error
                }
            });
        }
    };

    return (
        <button
            disabled={!timeSlotState.selectedSlot}
            className={`btn btn--primary btn--big${className ? ` ${className}` : ''}`}
            onClick={() => onNextClick()}
        >
            <span>{t('clinic_form.next')}</span>
        </button>
    );
}
